import { useState, useEffect } from 'react';
import {
  isVideoTransformDevice,
  BackgroundReplacementVideoFrameProcessor,
  DefaultVideoTransformDevice,
  Logger,
  VideoInputDevice,
  Device
} from 'amazon-chime-sdk-js';
import {
  useBackgroundBlur,
  useBackgroundReplacement,
  useVideoInputs,
  useAudioInputs,
  MeetingManager
} from 'amazon-chime-sdk-component-library-react';
import { Appointment } from 'vl-common/src/schemas/shared';
import useSendTwilioEvents from '@hooks/useSendTwilioEvents';

export function useChimeChangeBackground({
  meetingManager,
  logger,
  user_guid,
  appointment
}: {
  meetingManager: MeetingManager;
  logger: Logger;
  user_guid: string;
  appointment: Appointment;
}) {
  const [backgroundType, setBackgroundType] = useState('none');
  const { isBackgroundReplacementSupported, createBackgroundReplacementDevice } = useBackgroundReplacement();
  const { isBackgroundBlurSupported, createBackgroundBlurDevice } = useBackgroundBlur();
  const { devices: videoDevices, selectedDevice: selectedVideoDevice } = useVideoInputs();
  const { devices: audioDevices } = useAudioInputs();
  const sendTwilioEvents = useSendTwilioEvents();

  const imageReplacement = async ({
    intrinsicDevice,
    current,
    url
  }: {
    intrinsicDevice: Device;
    current: VideoInputDevice;
    url: string;
  }) => {
    const processors = [];
    const replacementDevice = await createBackgroundReplacementDevice(intrinsicDevice || (current as Device));
    const replacementIntrinsicDevice = await replacementDevice?.intrinsicDevice();
    if (isBackgroundReplacementSupported) {
      const image = await fetch(url);
      const imageBlob = await image.blob();
      const options = { imageBlob, filterCPUUtilization: 0 };
      const replacementProcessor = await BackgroundReplacementVideoFrameProcessor.create(undefined, options);
      if (replacementProcessor) {
        processors.push(replacementProcessor);
      }
    }
    if (processors?.length > 0) {
      current = new DefaultVideoTransformDevice(logger, replacementIntrinsicDevice, processors);
    }
    return current;
  };

  async function changeBackground() {
    try {
      let current = selectedVideoDevice;
      let intrinsicDevice = null;
      try {
        // selectedVideoDevice can be of type Device or VideoTransformDevice. Device does not have the property intrinsicDevice but VideoTransformDevice, which we are using here, does
        // @ts-ignore
        intrinsicDevice = await selectedVideoDevice?.intrinsicDevice();
      } catch (e) {
        console.log('intrinsicDevice err:', e);
      }
      if (isVideoTransformDevice(selectedVideoDevice)) {
        await selectedVideoDevice.stop();
      }
      switch (backgroundType) {
        case 'none': {
          if (isVideoTransformDevice(selectedVideoDevice)) {
            await selectedVideoDevice.stop();
            current = intrinsicDevice;
          }
          break;
        }
        case 'blur': {
          current = await createBackgroundBlurDevice(intrinsicDevice || current);
          break;
        }
        case 'HBS': {
          if (current) {
            current = await imageReplacement({
              intrinsicDevice,
              current,
              url: '/static/images/hbsuk_logo.png'
            });
          }
          break;
        }
        default: {
          console.log('default');
        }
      }
      if (current) {
        await meetingManager.startVideoInputDevice(current);
      }
    } catch (error) {
      console.error('Failed to toggle BackgroundReplacement', error);
      await sendTwilioEvents({
        userGuid: user_guid,
        appointmentId: appointment.appointment_id,
        event: 'USRLOG',
        message: `Failed to get video inputs: ${error?.toString()}`
      });
    }
  }
  useEffect(() => {
    changeBackground();
  }, [backgroundType]);

  return {
    isBackgroundBlurSupported,
    isBackgroundReplacementSupported,
    videoDevices,
    audioDevices,
    selectedVideoDevice,
    backgroundType,
    setBackgroundType
  };
}

export default useChimeChangeBackground;
